import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Input,
  Icon,
  Tooltip,
  message,
  Spin,
  Select,
  Button,
  Popover,
  Alert,
  Card,
  Table,
  Avatar,
  Tabs,
  Collapse,
  Modal,
  Badge
} from 'antd'
import classNames from 'classnames'
import {
  SortableContainer,
  SortableElement,
  sortableHandle,
  arrayMove as arrayMoveImmutable
} from 'react-sortable-hoc'

import StepTitle from '../StepTitle'
import StatusView from '../StatusView/StatusView'

import './ShotList.scss'
import {
  generateShots,
  mapCharacterToShots,
  getAvailableCharacters,
  groupShotsToScenes,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import AddCharacterModal from './components/CharacterMappingTable/AddCharacterModal'
import CharacterMappingTable from './components/CharacterMappingTable'
import LocalReferencesView from './components/LocalReferences'
import PropReferencesView from './components/PropReferences'

const { Option } = Select
const { Panel } = Collapse
const { TabPane } = Tabs

// Drag Handle Component
const DragHandle = sortableHandle(() => (
  <Tooltip title="Drag to reorder">
    <div className="drag-handle">
      <Icon type="drag" style={{ fontSize: '18px' }} />
    </div>
  </Tooltip>
))

// Character Highlighter Component (read-only, just highlights)
const CharacterHighlighter = ({ text, selectedCharacters = [] }) => {
  const highlightCharacters = text => {
    if (!text || typeof text !== 'string') return text

    let processedText = text
    const highlights = []

    const placeholderRegex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g
    let match
    while ((match = placeholderRegex.exec(text)) !== null) {
      highlights.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        isPlaceholder: true
      })
    }

    selectedCharacters.forEach(char => {
      if (char.current) {
        const currentName = char.current.replace(/^__|__$/g, '')
        const regex = new RegExp(`\\b${currentName}\\b`, 'g')
        let nameMatch
        while ((nameMatch = regex.exec(text)) !== null) {
          const isOverlapping = highlights.some(
            h => nameMatch.index >= h.start && nameMatch.index < h.end
          )
          if (!isOverlapping) {
            highlights.push({
              start: nameMatch.index,
              end: nameMatch.index + currentName.length,
              text: currentName,
              isPlaceholder: false
            })
          }
        }
      }
    })

    highlights.sort((a, b) => a.start - b.start)

    if (highlights.length === 0) return text

    const parts = []
    let lastIndex = 0

    highlights.forEach((highlight, idx) => {
      if (highlight.start > lastIndex) {
        parts.push(text.substring(lastIndex, highlight.start))
      }

      parts.push(
        <span key={`highlight-${idx}`} className="character-placeholder">
          {highlight.text}
        </span>
      )

      lastIndex = highlight.end
    })

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  return (
    <div className="character-highlighter">
      <div className="shot-direction-text">{highlightCharacters(text)}</div>
    </div>
  )
}

// Read-only display for other fields
const ReadOnlyField = ({ text }) => {
  return <div className="readonly-field">{text}</div>
}

// Shot Component (not sortable)
const Shot = ({
  shot,
  shotIndex,
  expandedPanelId,
  setExpandedPanelId,
  selectedCharacters
}) => {
  const { id, title, initial_image_prompt, video_prompt, dialogue } = shot
  const isExpanded = expandedPanelId === id

  return (
    <div className={classNames('shot-list-shot', { expanded: isExpanded })}>
      <div
        className="shot-header"
        onClick={() => setExpandedPanelId(isExpanded ? null : id)}
      >
        <div className="shot-title-container">
          <span>Shot {shotIndex + 1}</span>
          <Icon
            type="right"
            rotate={isExpanded ? 90 : 0}
            className="expand-icon"
          />
        </div>
      </div>

      <div
        className={classNames('shot-description-container', {
          expanded: isExpanded
        })}
      >
        <div className="shot-detail-field">
          <label>Title</label>
          <ReadOnlyField text={title} />
        </div>

        <div className="shot-detail-field">
          <label>Initial Image Prompt</label>
          <Card>
            <CharacterHighlighter
              text={initial_image_prompt}
              selectedCharacters={selectedCharacters}
            />
          </Card>
        </div>

        <div className="shot-detail-field">
          <label>Video Prompt</label>
          <Card>
            <CharacterHighlighter
              text={video_prompt}
              selectedCharacters={selectedCharacters}
            />
          </Card>
        </div>

        {Object.keys(dialogue || {}).length > 0 && (
          <div className="shot-detail-field">
            <label>Dialogue</label>
            <Card>
              {Object.entries(dialogue || {}).map(([character, line], idx) => (
                <div key={idx} className="dialogue-entry readonly">
                  <div className="dialogue-character">
                    <Icon type="message" style={{ fontSize: '12px' }} />
                    <CharacterHighlighter
                      text={character}
                      selectedCharacters={selectedCharacters}
                    />
                  </div>
                  <div className="dialogue-line">
                    <CharacterHighlighter
                      text={typeof line === 'object' ? line.text : line}
                      selectedCharacters={selectedCharacters}
                    />
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

// Sortable Scene Component
const SortableScene = SortableElement(
  ({
    scene,
    sceneIndex,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    return (
      <Card
        className="scene-card"
        title={
          <div
            className="scene-header"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <DragHandle />
            <span className="scene-title">{scene.title.toUpperCase()}</span>
            <Icon
              type="right"
              rotate={isExpanded ? 90 : 0}
              className="scene-expand-icon"
              style={{ marginLeft: 'auto' }}
            />
          </div>
        }
      >
        <div className="scene-info">
          <div className="scene-detail-field">
            <label>Location</label>
            <ReadOnlyField text={scene.description} />
          </div>

          {isExpanded && (
            <div className="scene-shots">
              <h4>Shots ({scene.shots.length})</h4>
              {scene.shots.map((shot, index) => (
                <Shot
                  key={`${scene.id}-shot-${index}`}
                  shot={shot}
                  shotIndex={index}
                  expandedPanelId={expandedPanelId}
                  setExpandedPanelId={setExpandedPanelId}
                  selectedCharacters={selectedCharacters}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    )
  }
)

// Sortable Scene List Component
const SortableSceneList = SortableContainer(
  ({
    scenes,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters,
    isExpanded,
    onToggleExpand
  }) => {
    return (
      <Card
        className="expandable-card"
        title={
          <div className="expandable-card-header" onClick={onToggleExpand}>
            <Icon
              type="right"
              className={classNames('expand-icon', {
                'is-expanded': isExpanded
              })}
            />
            <span className="header-title">Scenes</span>
            <span className="header-count">{scenes.length}</span>
          </div>
        }
        bodyStyle={{
          padding: isExpanded ? '24px' : '0',
          display: isExpanded ? 'block' : 'none',
          overflow: 'visible'
        }}
        style={{ marginBottom: '24px' }}
      >
        <div className="scene-list">
          {scenes.map((scene, index) => (
            <SortableScene
              key={`scene-${index}`}
              index={index}
              scene={scene}
              sceneIndex={index}
              expandedPanelId={expandedPanelId}
              setExpandedPanelId={setExpandedPanelId}
              selectedCharacters={selectedCharacters}
            />
          ))}
        </div>
      </Card>
    )
  }
)

// Custom function to render the dragged element
const dragHandleHelper = (node, parent) => {
  const clone = node.cloneNode(true)

  // Find and remove expansion details during drag
  const shotsContainer = clone.querySelector('.scene-shots')
  if (shotsContainer) {
    shotsContainer.remove()
  }

  clone.className += ' dragging-scene'
  parent.appendChild(clone)

  return clone
}

const ShotList = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onValidationChange,
    isGeneratingLocationReference,
    shotsGenerationProgress,
    onRegenerateLocationReference,
    onAddLocationReference,
    onEditLocationReference,
    onBatchUpdateLocations,
    isClusteringAudio,
    isFetchingCharacters,
    characters: propsCharacters,
    setCharacters: setPropsCharacters,
    fetchCharacters,
    locationGenerationStatus,
    locationGenerationProgress,
    isGeneratingPropReference,
    propGenerationProgress,
    propGenerationStatus,
    onRegeneratePropReference,
    onAddPropReference,
    onEditPropReference,
    onBatchUpdateProps,
    videoId,
    fetchingShots,
    isClustering,
    clusteringProgress
  } = props

  const [scenes, setScenes] = useState([])
  const [expandedPanelId, setExpandedPanelId] = useState(null)

  const [activeTab, setActiveTab] = useState('1')
  const [initialCharacters, setInitialCharacters] = useState([])
  const [changedCharacters, setChangedCharacters] = useState([])

  const [isScenesExpanded, setIsScenesExpanded] = useState(true)
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false)

  const {
    characterList = [],
    shotList = [],
    shotLoading = false,
    shotsApiRes = {},
    selectedCharacter = []
  } = formData

  const characters = propsCharacters || []

  useEffect(() => {
    if (selectedCharacter && selectedCharacter.length > 0) {
      setChangedCharacters(selectedCharacter)
      setInitialCharacters(selectedCharacter)
    }

    if (shotList.length > 0) {
      setScenes(shotList)
    }
  }, [])

  useEffect(() => {
    if (shotList.length > 0) {
      setScenes(shotList)
    }
  }, [shotList])

  useEffect(() => {
    if (characters.length > 0) {
      setFormData({ ...formData, characterList: characters })
    }
  }, [characters])

  useEffect(() => {
    if (changedCharacters.length > 0) {
      setFormData({ ...formData, selectedCharacter: changedCharacters })
    }
  }, [changedCharacters])

  // Enhanced function to extract characters from both original and current text
  function extractCharacters(scenesArray) {
    const characterSet = new Set()

    // Regex to match both **character1** and __character1__ formats
    const characterRegex = /(?:\*\*character\d+\*\*|__character\d+__)/g

    function searchInValue(value) {
      if (typeof value === 'string') {
        const matches = value.match(characterRegex)
        if (matches) {
          matches.forEach(match => characterSet.add(match))
        }
      } else if (Array.isArray(value)) {
        // Handle arrays
        value.forEach(item => searchInValue(item))
      } else if (typeof value === 'object' && value !== null) {
        // Handle objects
        Object.values(value).forEach(val => searchInValue(val))
      }
    }

    // Search through the entire scenes array
    searchInValue(scenesArray)

    return Array.from(characterSet)
  }

  // Enhanced character change handler that works from CharacterMappingTable
  const handleCharacterChange = (
    oldCharacter,
    newCharacter,
    _firstCharacter,
    characterId
  ) => {
    console.log(`Replacing "${oldCharacter}" with "${newCharacter}"`)

    const oldCharWithUnderscores = `__${oldCharacter.replace(/^__|__$/g, '')}__`
    const newCharWithUnderscores = `__${newCharacter}__`

    // Update character mappings in selectedCharacter
    let updatedChars = [...changedCharacters]
    const existingCharIndex = updatedChars.findIndex(
      char =>
        char.original === oldCharWithUnderscores ||
        (char.original &&
          char.original.replace(/^__|__$/g, '') ===
            oldCharacter.replace(/^__|__$/g, ''))
    )

    if (existingCharIndex !== -1) {
      updatedChars[existingCharIndex] = {
        ...updatedChars[existingCharIndex],
        last: updatedChars[existingCharIndex].current,
        current: newCharWithUnderscores,
        tag: characterId
      }
    } else {
      updatedChars.push({
        original: oldCharWithUnderscores,
        last: oldCharWithUnderscores,
        current: newCharWithUnderscores,
        tag: characterId
      })
    }

    setChangedCharacters(updatedChars)

    message.success(
      `Changed "${oldCharacter}" to "${newCharacter}" throughout all scenes and shots`
    )
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedScenes = arrayMoveImmutable(scenes, oldIndex, newIndex)
    setScenes(updatedScenes)
    setFormData(prev => ({ ...prev, shotList: updatedScenes }))
  }

  // Helper to check if characterMap is valid and has entries
  const isCharacterMapValid = map => {
    if (!map) return false
    if (typeof map === 'string') {
      try {
        const parsed = JSON.parse(map)
        return (
          parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0
        )
      } catch {
        return false
      }
    }
    return typeof map === 'object' && Object.keys(map).length > 0
  }

  // Get total characters - prioritize characterMap keys if valid, otherwise extract from shots
  const totalCharacters = useMemo(() => {
    const charMap = formData.characterMap

    // Use characterMap keys if it's a valid non-empty object
    if (isCharacterMapValid(charMap)) {
      const mapToUse =
        typeof charMap === 'string' ? JSON.parse(charMap) : charMap
      return Object.keys(mapToUse)
    }

    // Fallback: extract characters from shots
    const originalChars = extractCharacters(shotList)
    return Array.from(new Set(originalChars))
  }, [formData.characterMap, shotList])

  const isAllCharactersChanged =
    totalCharacters.length === 0 ||
    totalCharacters.every(char => {
      const normalizedChar = char.replace(/^__|__$/g, '')
      return changedCharacters.some(c => {
        const normalizedC = c.original.replace(/^__|__$/g, '')
        return normalizedC === normalizedChar
      })
    })

  const allLocationsGenerated = useMemo(() => {
    const locMap = formData.locationMap || {}
    const locRef = formData.locationReference || {}
    const keys = Object.keys(locMap)
    if (keys.length === 0) return true
    return keys.every(key => {
      const ref = locRef[key] || locRef[`__${key}__`]
      return ref && (ref.uri || ref.url)
    })
  }, [formData.locationMap, formData.locationReference])

  const allPropsGenerated = useMemo(() => {
    const propMap = formData.propMap || {}
    const propRef = formData.props_reference || {}
    const keys = Object.keys(propMap)
    if (keys.length === 0) return true
    return keys.every(key => {
      const ref = propRef[key]
      return ref && (ref.uri || ref.url)
    })
  }, [formData.propMap, formData.props_reference])

  const allOutfitsMapped = useMemo(() => {
    if (totalCharacters.length === 0) return true

    const wardrobe = formData.wardrobe || {}
    const outfitsRef = formData.outfits_reference || {}

    return totalCharacters.every(char => {
      const charOutfits = wardrobe[char] || []
      if (charOutfits.length === 0) return true

      const charRefs = outfitsRef[char] || {}

      // Check if every outfit in wardrobe has a reference
      return charOutfits.every(outfit => charRefs[outfit])
    })
  }, [totalCharacters, formData.wardrobe, formData.outfits_reference])

  const unmappedCharactersCount = useMemo(() => {
    return totalCharacters.filter(char => {
      const normalizedChar = char.replace(/^__|__$/g, '')
      return !changedCharacters.some(c => {
        const normalizedC = c.original.replace(/^__|__$/g, '')
        return normalizedC === normalizedChar
      })
    }).length
  }, [totalCharacters, changedCharacters])

  const ungeneratedLocationsCount = useMemo(() => {
    const locMap = formData.locationMap || {}
    const locRef = formData.locationReference || {}
    const keys = Object.keys(locMap)
    return keys.filter(key => {
      const ref = locRef[key] || locRef[`__${key}__`]
      return !(ref && (ref.uri || ref.url))
    }).length
  }, [formData.locationMap, formData.locationReference])

  const ungeneratedPropsCount = useMemo(() => {
    const propMap = formData.propMap || {}
    const propRef = formData.props_reference || {}
    const keys = Object.keys(propMap)
    return keys.filter(key => {
      const ref = propRef[key]
      return !(ref && (ref.uri || ref.url))
    }).length
  }, [formData.propMap, formData.props_reference])

  const isValid = useMemo(() => {
    const hasShots = scenes.length > 0
    return (
      hasShots &&
      isAllCharactersChanged &&
      allLocationsGenerated &&
      allPropsGenerated &&
      allOutfitsMapped
    )
  }, [
    scenes,
    isAllCharactersChanged,
    allLocationsGenerated,
    allPropsGenerated,
    allOutfitsMapped
  ])

  console.log({
    isValid,
    isAllCharactersChanged,
    allLocationsGenerated,
    allPropsGenerated,
    allOutfitsMapped,
    totalCharacters
  })

  const hasChanges = useMemo(() => {
    if (changedCharacters.length !== initialCharacters.length) return true

    const normalize = arr =>
      arr
        .slice()
        .sort((a, b) => a.original.localeCompare(b.original))
        .map(({ original, current }) => ({ original, current }))

    const a = normalize(changedCharacters)
    const b = normalize(initialCharacters)

    return JSON.stringify(a) !== JSON.stringify(b)
  }, [changedCharacters, initialCharacters])

  useEffect(() => {
    onValidationChange(isValid)
  }, [isValid])

  const handleAddCharacter = () => {
    setShowAddCharacterModal(true)
  }

  const handleCharacterAddition = _character => {
    // Refresh characters from the parent hook
    if (fetchCharacters) {
      fetchCharacters(true) // force refresh
    }
  }

  // Handler to add a new character to the characterMap (legacy format for now)
  const handleAddNewCharacter = (characterId, characterName) => {
    setFormData(prev => ({
      ...prev,
      characterMap: {
        ...(prev.characterMap || {}),
        [characterId]: characterName
      }
    }))
  }

  // Handler to delete a character from the characterMap
  const handleDeleteCharacter = characterKey => {
    setFormData(prev => {
      const updatedMap = { ...(prev.characterMap || {}) }
      delete updatedMap[characterKey]
      return {
        ...prev,
        characterMap: updatedMap
      }
    })

    // Also remove from changedCharacters if present
    setChangedCharacters(prev =>
      prev.filter(char => char.original !== characterKey)
    )
  }

  const handleClearShotMedia = (shotIds, clearFinalVideo = false) => {
    if (!shotIds || shotIds.length === 0) return

    setFormData(prev => {
      const updatedShotList = prev.shotList.map(scene => ({
        ...scene,
        shots: scene.shots.map(shot => {
          if (shotIds.includes(shot.id)) {
            return {
              ...shot,
              imageSrc: null,
              vidSrc: null
            }
          }
          return shot
        })
      }))

      return {
        ...prev,
        shotList: updatedShotList,
        finalVideo: clearFinalVideo ? null : prev.finalVideo
      }
    })
  }

  const handleWardrobeSave = (characterKey, updatedTagsData) => {
    setFormData(prev => ({
      ...prev,
      outfits_reference: {
        ...(prev.outfits_reference || {}),
        [characterKey]: updatedTagsData
      }
    }))
  }

  const handleRegenerateShots = () => {
    Modal.confirm({
      title: 'Are you sure you want to regenerate shots?',
      content:
        'This will overwrite your existing shots and any edits you have made.',
      okText: 'Yes, Regenerate',
      cancelText: 'Cancel',
      onOk: () => {
        props.onRegenerateShots()
      },
      centered: true
    })
  }

  if (shotLoading || fetchingShots) {
    const loadingTitle = isFetchingCharacters
      ? 'Fetching characters...'
      : isClustering
      ? 'Clustering scenes...'
      : isGeneratingLocationReference
      ? 'Generating locations...'
      : 'Generating your shots...'

    const loadingProgress = isFetchingCharacters
      ? null
      : isClustering
      ? clusteringProgress
      : isGeneratingLocationReference
      ? locationGenerationProgress
      : shotsGenerationProgress

    return (
      <div className="shot-list-shots">
        <StatusView
          status="loading"
          title={loadingTitle}
          subTitle="Please wait! This may take a few minutes."
          progress={loadingProgress}
          inline
        />
      </div>
    )
  }

  return (
    <div className="shot-list-shots">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}
      >
        <StepTitle
          iconType="ordered-list"
          title="Shot List"
          description="Map characters first, then drag to reorder scenes."
          isGeneratingLocationReference={isGeneratingLocationReference}
        />

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            onClick={handleRegenerateShots}
            loading={shotLoading}
            icon="reload"
            size="large"
          >
            Regenerate Shots
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginTop: 16 }}
      >
        <TabPane
          tab={
            <Tooltip
              title={
                unmappedCharactersCount > 0
                  ? `${unmappedCharactersCount} characters need mapping`
                  : 'All characters mapped'
              }
            >
              <Badge
                count={unmappedCharactersCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    unmappedCharactersCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color: unmappedCharactersCount > 0 ? '#d4380d' : 'inherit',
                    fontWeight: unmappedCharactersCount > 0 ? 600 : 'normal',
                    paddingRight: unmappedCharactersCount > 0 ? 8 : 0
                  }}
                >
                  {unmappedCharactersCount > 0 && (
                    <Icon type="warning" style={{ marginRight: 8 }} />
                  )}
                  Shot List
                </span>
              </Badge>
            </Tooltip>
          }
          key="1"
        >
          {totalCharacters.length > 0 && (
            <CharacterMappingTable
              formData={formData}
              setFormData={setFormData}
              placeholderCharacters={totalCharacters}
              characters={characters}
              onCharacterChange={handleCharacterChange}
              changedCharacters={changedCharacters}
              onAddCharacter={handleAddCharacter}
              isFetchingCharacters={isFetchingCharacters}
              characterMap={formData.characterMap || {}}
              isAllCharactersChanged={isAllCharactersChanged}
              shotList={shotList}
              onAddNewCharacter={handleAddNewCharacter}
              onDeleteCharacter={handleDeleteCharacter}
              onClearShotMedia={handleClearShotMedia}
              fetchCharacters={fetchCharacters}
              wardrobe={formData.wardrobe}
              outfitMap={formData.outfitMap}
              outfitsReference={formData.outfits_reference}
              onWardrobeSave={handleWardrobeSave}
              videoId={videoId}
            />
          )}

          <SortableSceneList
            scenes={scenes}
            expandedPanelId={expandedPanelId}
            setExpandedPanelId={setExpandedPanelId}
            characters={characters}
            onCharacterChange={handleCharacterChange}
            onSortEnd={onSortEnd}
            useDragHandle
            axis="y"
            helperClass="dragging-scene"
            helperContainer={document.body}
            helper={dragHandleHelper}
            isExpanded={isScenesExpanded}
            onToggleExpand={() => setIsScenesExpanded(!isScenesExpanded)}
            selectedCharacters={changedCharacters}
          />
        </TabPane>

        <TabPane
          tab={
            <Tooltip
              title={
                ungeneratedLocationsCount > 0
                  ? `${ungeneratedLocationsCount} locations need generation`
                  : 'All locations generated'
              }
            >
              <Badge
                count={ungeneratedLocationsCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    ungeneratedLocationsCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color:
                      ungeneratedLocationsCount > 0 &&
                      !isGeneratingLocationReference
                        ? '#d4380d'
                        : 'inherit',
                    fontWeight:
                      ungeneratedLocationsCount > 0 &&
                      !isGeneratingLocationReference
                        ? 600
                        : 'normal',
                    paddingRight: ungeneratedLocationsCount > 0 ? 8 : 0
                  }}
                >
                  {isGeneratingLocationReference && (
                    <Icon type="loading" style={{ marginRight: 8 }} />
                  )}
                  {ungeneratedLocationsCount > 0 &&
                    !isGeneratingLocationReference && (
                      <Icon type="warning" style={{ marginRight: 8 }} />
                    )}
                  Locations
                </span>
              </Badge>
            </Tooltip>
          }
          key="2"
        >
          {(formData.locationMap || formData.locationReference) && (
            <LocalReferencesView
              formData={formData}
              locationMap={formData.locationMap || {}}
              locationReference={formData.locationReference || {}}
              scenes={scenes}
              setFormData={setFormData}
              onRegenerateLocationReference={onRegenerateLocationReference}
              onAddLocationReference={onAddLocationReference}
              onEditLocationReference={onEditLocationReference}
              isGeneratingLocationReference={isGeneratingLocationReference}
              onBatchUpdateLocations={onBatchUpdateLocations}
              locationGenerationProgress={locationGenerationProgress}
              locationGenerationStatus={locationGenerationStatus}
              videoId={videoId}
            />
          )}
        </TabPane>

        <TabPane
          tab={
            <Tooltip
              title={
                ungeneratedPropsCount > 0
                  ? `${ungeneratedPropsCount} props need generation`
                  : 'All props generated'
              }
            >
              <Badge
                count={ungeneratedPropsCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    ungeneratedPropsCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color:
                      ungeneratedPropsCount > 0 && !isGeneratingPropReference
                        ? '#d4380d'
                        : 'inherit',
                    fontWeight:
                      ungeneratedPropsCount > 0 && !isGeneratingPropReference
                        ? 600
                        : 'normal',
                    paddingRight: ungeneratedPropsCount > 0 ? 8 : 0
                  }}
                >
                  {isGeneratingPropReference && (
                    <Icon type="loading" style={{ marginRight: 8 }} />
                  )}
                  {ungeneratedPropsCount > 0 && !isGeneratingPropReference && (
                    <Icon type="warning" style={{ marginRight: 8 }} />
                  )}
                  Props
                </span>
              </Badge>
            </Tooltip>
          }
          key="3"
        >
          {(formData.propMap || formData.props_reference) && (
            <PropReferencesView
              formData={formData}
              propMap={formData.propMap || {}}
              propReference={formData.props_reference || {}}
              scenes={scenes}
              setFormData={setFormData}
              onRegeneratePropReference={onRegeneratePropReference}
              onAddPropReference={onAddPropReference}
              onEditPropReference={onEditPropReference}
              isGeneratingPropReference={isGeneratingPropReference}
              onBatchUpdateProps={onBatchUpdateProps}
              propGenerationProgress={propGenerationProgress}
              propGenerationStatus={propGenerationStatus}
              videoId={videoId}
            />
          )}
        </TabPane>

        <TabPane tab="Story" key="4">
          <Card title="Generated Story" style={{ marginBottom: '20px' }}>
            {formData.voiceoverText ? (
              <div>
                <p>{formData.voiceoverText}</p>
              </div>
            ) : (
              <p style={{ color: '#999' }}>No story text available.</p>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {showAddCharacterModal && (
        <AddCharacterModal
          onClose={() => setShowAddCharacterModal(false)}
          onCreate={handleCharacterAddition}
          videoId={videoId}
        />
      )}
    </div>
  )
})

export default ShotList
